import React, { FunctionComponent } from 'react';
import AtlassianSwitcher from '@atlaskit/atlassian-switcher';
import { IntlProvider } from 'react-intl';
import FabricAnalyticsListeners, {
  FabricChannel,
} from '@atlaskit/analytics-listeners';
import { Analytics } from '@trello/atlassian-analytics';
import { N300, N800 } from '@trello/colors';
import { ProductKeys } from '@atlassiansox/product-store-react';

import {
  JoinableSitesResponse,
  AvailableProductsResponse,
} from '@atlaskit/atlassian-switcher/dist/cjs/types';
import { ExportedDataProvider } from '@atlaskit/atlassian-switcher/dist/cjs/providers/create-data-provider';
import { AnalyticsContext } from '@atlaskit/analytics-next';

export enum AtlassianSwitcherVariations {
  CONTROL = 'control',
  NOT_ENROLLED = 'not-enrolled',
  EXPERIMENT = 'experiment',
}

const trelloTheme = {
  primaryTextColor: N800,
  secondaryTextColor: N300,
  primaryHoverBackgroundColor: '#E0E2E5',
  secondaryHoverBackgroundColor: '#F5F6F7',
};

interface AtlassianAppSwitcherProps {
  aaId?: string;
  triggerXFlow: (productKey: ProductKeys) => void;
  onDiscoverMoreClicked: () => void;
  isDefaultEditionFreeExperimentEnabled: boolean;
  joinableSitesDataProvider:
    | ExportedDataProvider<JoinableSitesResponse>
    | undefined;
  availableProductsDataProvider:
    | ExportedDataProvider<AvailableProductsResponse>
    | undefined;
  nonAaMastered: boolean;
  defaultSignupEmail?: string | null;
  locale?: string;
  onClose: () => void;
}

export const AtlassianAppSwitcher: FunctionComponent<AtlassianAppSwitcherProps> = ({
  aaId,
  triggerXFlow,
  onDiscoverMoreClicked,
  joinableSitesDataProvider,
  availableProductsDataProvider,
  isDefaultEditionFreeExperimentEnabled = false,
  nonAaMastered,
  defaultSignupEmail,
  locale = 'en',
  onClose,
}) => {
  const discoverySwitcherProps = {
    isDefaultEditionFreeExperimentEnabled,
    isDiscoverMoreForEveryoneEnabled: true,
    isDiscoverSectionEnabled: true,
    triggerXFlow,
    onDiscoverMoreClicked,
    recommendationsFeatureFlags: {
      isProductStoreInTrelloEnabled: true,
    },
    joinableSitesDataProvider,
    availableProductsDataProvider,
  };
  const analyticsContextData = {
    navigationCtx: {
      attributes: {
        aaId,
        isAaMastered: !nonAaMastered,
      },
    },
  };

  return (
    <IntlProvider locale={locale}>
      {
        <FabricAnalyticsListeners
          client={Analytics.dangerouslyGetAnalyticsWebClient()}
          excludedChannels={[
            FabricChannel.atlaskit,
            FabricChannel.elements,
            FabricChannel.editor,
            FabricChannel.media,
          ]}
        >
          <AnalyticsContext data={analyticsContextData}>
            <AtlassianSwitcher
              product="trello"
              appearance="standalone"
              theme={trelloTheme}
              joinableSitesSwitcherProps
              {...discoverySwitcherProps}
              nonAaMastered={nonAaMastered}
              defaultSignupEmail={
                defaultSignupEmail === null ? undefined : defaultSignupEmail
              }
              onClose={onClose}
            />
          </AnalyticsContext>
        </FabricAnalyticsListeners>
      }
    </IntlProvider>
  );
};
